import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFeatureFlag } from '../../services/featureFlag.service';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, Save, ChevronDown, ChevronRight, Layers, Tags } from 'lucide-react';
import { toast } from 'sonner';
import { usePipelineNodes } from '../../hooks/usePipelineNodes';
import { usePipelineNodeRelationships, type PipelineNodeRelationship } from '../../hooks/usePipelineNodeRelationships';
import { useMeasures } from '../../hooks/useMeasures';
import { useDimensionsList } from '../../hooks/useDimension';
import { useAllTemplates } from '../../hooks/useTemplates';
import { templateService } from '../../services/template.service';
import type { Template } from '../../services/template.service';
import type { PipelineNode } from '../../services/pipelineNode.service';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

// Convert a label/name to a taxonomic segment: uppercase, spaces to underscores, strip non-alphanumeric
function toTaxSegment(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
}

export default function ExportTemplate() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!templateId;
  const isTemplateManagementEnabled = useFeatureFlag('template-management', false);

  const { data: nodesData, isLoading: nodesLoading } = usePipelineNodes({ pageSize: 500 });
  const { data: relationships, isLoading: relsLoading } = usePipelineNodeRelationships();
  const { data: allTemplates } = useAllTemplates();
  const { data: measuresData, isLoading: measuresLoading } = useMeasures({ pageSize: 500 });
  const { data: dimensionsData, isLoading: dimensionsLoading } = useDimensionsList({ pageSize: 500 });

  // Form state
  const [templateName, setTemplateName] = useState('');
  const [taxonomicId, setTaxonomicId] = useState('');
  const [templateType, setTemplateType] = useState<string>('');
  const [dataModelTemplateId, setDataModelTemplateId] = useState<string>('');
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [selectedFieldIds, setSelectedFieldIds] = useState<Record<string, Set<string>>>({});
  const [nodeTaxonomicIds, setNodeTaxonomicIds] = useState<Record<string, string>>({});
  const [fieldTaxonomicIds, setFieldTaxonomicIds] = useState<Record<string, Record<string, string>>>({});
  const [selectedRelationshipIds, setSelectedRelationshipIds] = useState<Set<string>>(new Set());
  const [relationshipTaxonomicIds, setRelationshipTaxonomicIds] = useState<Record<string, string>>({});
  const [selectedMeasureIds, setSelectedMeasureIds] = useState<Set<string>>(new Set());
  const [selectedDimensionIds, setSelectedDimensionIds] = useState<Set<string>>(new Set());
  const [measureTaxonomicIds, setMeasureTaxonomicIds] = useState<Record<string, string>>({});
  const [dimensionTaxonomicIds, setDimensionTaxonomicIds] = useState<Record<string, string>>({});
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [savingTaxonomicIds, setSavingTaxonomicIds] = useState(false);
  const [showTaxIdAlert, setShowTaxIdAlert] = useState(false);

  // Load existing template data when editing
  // Load existing template selections when editing
  useEffect(() => {
    if (isEditing && allTemplates) {
      const existing = allTemplates.find(t => t.id === templateId);
      if (existing) {
        setTemplateName(existing.name);
        setTaxonomicId(existing.taxonomic_id || '');
        setTemplateType(existing.type);
        setDataModelTemplateId(existing.data_model_template_id || '');

        const nodeIds = new Set(existing.included_nodes.map(n => n.pipeline_node_id));
        setSelectedNodeIds(nodeIds);

        const fieldIds: Record<string, Set<string>> = {};
        existing.included_nodes.forEach(n => {
          fieldIds[n.pipeline_node_id] = new Set(n.included_field_ids);
        });
        setSelectedFieldIds(fieldIds);

        setSelectedRelationshipIds(new Set(existing.include_relationship_ids));

        if (existing.include_measure_ids) {
          setSelectedMeasureIds(new Set(existing.include_measure_ids));
        }
        if (existing.include_dimension_ids) {
          setSelectedDimensionIds(new Set(existing.include_dimension_ids));
        }
      }
    }
  }, [isEditing, templateId, allTemplates]);

  // Load taxonomic IDs from pipeline nodes and relationships
  useEffect(() => {
    if (nodesData?.records) {
      const ntIds: Record<string, string> = {};
      const ftIds: Record<string, Record<string, string>> = {};
      nodesData.records.forEach(node => {
        if (node.taxonomic_id) {
          ntIds[node.id] = node.taxonomic_id;
        }
        if (node.fields) {
          const fieldMap: Record<string, string> = {};
          node.fields.forEach(f => {
            if (f.id && f.taxonomic_id) {
              fieldMap[f.id] = f.taxonomic_id;
            }
          });
          if (Object.keys(fieldMap).length > 0) {
            ftIds[node.id] = fieldMap;
          }
        }
      });
      setNodeTaxonomicIds(ntIds);
      setFieldTaxonomicIds(ftIds);
    }

    if (relationships) {
      const rtIds: Record<string, string> = {};
      relationships.forEach(rel => {
        if (rel.taxonomic_id) {
          rtIds[rel.id] = rel.taxonomic_id;
        }
      });
      setRelationshipTaxonomicIds(rtIds);
    }

    if (measuresData?.records) {
      const mtIds: Record<string, string> = {};
      measuresData.records.forEach(m => {
        if (m.taxonomic_id) {
          mtIds[m.id] = m.taxonomic_id;
        }
      });
      setMeasureTaxonomicIds(mtIds);
    }

    if (dimensionsData?.records) {
      const dtIds: Record<string, string> = {};
      dimensionsData.records.forEach(d => {
        if (d.taxonomic_id) {
          dtIds[d.id] = d.taxonomic_id;
        }
      });
      setDimensionTaxonomicIds(dtIds);
    }
  }, [nodesData?.records, relationships, measuresData?.records, dimensionsData?.records]);

  const nodes = useMemo(() => nodesData?.records || [], [nodesData?.records]);

  // Filter nodes based on template type
  const filteredNodes = useMemo(() => {
    if (templateType === 'DATA_SOURCE') {
      return nodes.filter(n => ['SOURCE', 'STAGING', 'CUSTOM'].includes(n.node_type));
    }
    if (templateType === 'DATA_MODEL') {
      return nodes.filter(n => ['DIMENSION', 'DATE_DIMENSION'].includes(n.node_type));
    }
    return nodes.filter(n => n.node_type !== 'SOURCE');
  }, [nodes, templateType]);

  // Filter relationships to only include those between selected nodes
  const filteredRelationships = useMemo(() => {
    if (!relationships) return [];
    return relationships.filter(r =>
      selectedNodeIds.has(r.parent_node_id) || selectedNodeIds.has(r.child_node_id)
    );
  }, [relationships, selectedNodeIds]);

  const nodeNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    nodes.forEach(n => { m[n.id] = n.label || n.name || n.id; });
    return m;
  }, [nodes]);

  const dataModelTemplates = useMemo(() => {
    return (allTemplates || []).filter(t => t.type === 'DATA_MODEL');
  }, [allTemplates]);

  const toggleNode = useCallback((nodeId: string) => {
    setSelectedNodeIds(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const toggleField = useCallback((nodeId: string, fieldId: string) => {
    setSelectedFieldIds(prev => {
      const next = { ...prev };
      const fieldSet = new Set(next[nodeId] || []);
      if (fieldSet.has(fieldId)) {
        fieldSet.delete(fieldId);
      } else {
        fieldSet.add(fieldId);
      }
      next[nodeId] = fieldSet;
      return next;
    });
  }, []);

  const includeAllFields = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node?.fields) return;
    setSelectedFieldIds(prev => {
      const next = { ...prev };
      next[nodeId] = new Set(node.fields!.map(f => f.id!).filter(Boolean));
      return next;
    });
  }, [nodes]);

  const setAllTaxonomicIds = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node?.fields) return;
    const nodeTaxId = nodeTaxonomicIds[nodeId];
    if (!nodeTaxId) {
      // If the node doesn't have a tax ID, we can't derive field tax IDs
      toast.error('Set a taxonomic ID on this node first');
      return;
    }
    setFieldTaxonomicIds(prev => {
      const next = { ...prev };
      const fieldMap: Record<string, string> = { ...(next[nodeId] || {}) };
      node.fields!.forEach(f => {
        if (f.id && !fieldMap[f.id]) {
          const fieldSegment = toTaxSegment(f.name || f.label || '');
          fieldMap[f.id] = `${nodeTaxId}::FIELD:${fieldSegment}`;
        }
      });
      next[nodeId] = fieldMap;
      return next;
    });
  }, [nodes, nodeTaxonomicIds]);

  // Global: set all taxonomic IDs for all selected nodes and their fields
  const setAllTaxonomicIdsGlobal = useCallback(() => {
    if (!taxonomicId) {
      setShowTaxIdAlert(true);
      return;
    }

    const newNodeTaxIds: Record<string, string> = { ...nodeTaxonomicIds };
    const newFieldTaxIds: Record<string, Record<string, string>> = { ...fieldTaxonomicIds };

    selectedNodeIds.forEach(nodeId => {
      const node = nodes.find(n => n.id === nodeId);
      if (!node) return;

      // Set node taxonomic ID
      // Only set node taxonomic ID if not already set
      const nodeSegment = toTaxSegment(node.name || node.label || '');
      const nodeTaxId = newNodeTaxIds[nodeId] || `${taxonomicId}::NODE:${nodeSegment}`;
      newNodeTaxIds[nodeId] = nodeTaxId;

      // Set field taxonomic IDs only for fields that don't already have one
      if (node.fields) {
        const fieldMap: Record<string, string> = { ...(newFieldTaxIds[nodeId] || {}) };
        node.fields.forEach(f => {
          if (f.id && !fieldMap[f.id]) {
            const fieldSegment = toTaxSegment(f.name || f.label || '');
            fieldMap[f.id] = `${nodeTaxId}::FIELD:${fieldSegment}`;
          }
        });
        newFieldTaxIds[nodeId] = fieldMap;
      }
    });

    // Set relationship taxonomic IDs
    const newRelTaxIds: Record<string, string> = { ...relationshipTaxonomicIds };
    selectedRelationshipIds.forEach(relId => {
      const rel = relationships?.find(r => r.id === relId);
      if (!rel) return;

      let relSegment: string;
      if (rel.name) {
        relSegment = toTaxSegment(rel.name);
      } else {
        const parentName = toTaxSegment(nodeNameMap[rel.parent_node_id] || rel.parent_node_id);
        const childName = toTaxSegment(nodeNameMap[rel.child_node_id] || rel.child_node_id);
        relSegment = `${parentName}_TO_${childName}`;
      }
      if (!newRelTaxIds[relId]) {
        newRelTaxIds[relId] = `${taxonomicId}::REL:${relSegment}`;
      }
    });

    // Set measure taxonomic IDs
    const newMeasureTaxIds: Record<string, string> = { ...measureTaxonomicIds };
    selectedMeasureIds.forEach(measureId => {
      const measure = measuresData?.records?.find(m => m.id === measureId);
      if (!measure) return;
      if (!newMeasureTaxIds[measureId]) {
        const segment = toTaxSegment(measure.name || '');
        newMeasureTaxIds[measureId] = `${taxonomicId}::MEASURE:${segment}`;
      }
    });
    setMeasureTaxonomicIds(newMeasureTaxIds);

    // Set dimension taxonomic IDs
    const newDimTaxIds: Record<string, string> = { ...dimensionTaxonomicIds };
    selectedDimensionIds.forEach(dimId => {
      const dim = dimensionsData?.records?.find(d => d.id === dimId);
      if (!dim) return;
      if (!newDimTaxIds[dimId]) {
        const segment = toTaxSegment(dim.name || '');
        newDimTaxIds[dimId] = `${taxonomicId}::DIM:${segment}`;
      }
    });
    setDimensionTaxonomicIds(newDimTaxIds);

    setNodeTaxonomicIds(newNodeTaxIds);
    setFieldTaxonomicIds(newFieldTaxIds);
    setRelationshipTaxonomicIds(newRelTaxIds);
    toast.success('Taxonomic IDs set for all selected nodes, fields, relationships, measures, and dimensions');
  }, [taxonomicId, selectedNodeIds, selectedRelationshipIds, selectedMeasureIds, selectedDimensionIds, nodes, relationships, measuresData, dimensionsData, nodeNameMap, nodeTaxonomicIds, fieldTaxonomicIds, relationshipTaxonomicIds, measureTaxonomicIds, dimensionTaxonomicIds]);

  const toggleRelationship = useCallback((relId: string) => {
    setSelectedRelationshipIds(prev => {
      const next = new Set(prev);
      if (next.has(relId)) {
        next.delete(relId);
      } else {
        next.add(relId);
      }
      return next;
    });
  }, []);

  const toggleMeasure = useCallback((measureId: string) => {
    setSelectedMeasureIds(prev => {
      const next = new Set(prev);
      if (next.has(measureId)) next.delete(measureId);
      else next.add(measureId);
      return next;
    });
  }, []);

  const toggleDimension = useCallback((dimensionId: string) => {
    setSelectedDimensionIds(prev => {
      const next = new Set(prev);
      if (next.has(dimensionId)) next.delete(dimensionId);
      else next.add(dimensionId);
      return next;
    });
  }, []);

  const toggleNodeExpanded = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Build form data for save
  const buildFormData = useCallback(() => {
    const data: Record<string, any> = {
      template_name: templateName,
      taxonomic_id: taxonomicId,
      template_type: templateType,
    };

    if (dataModelTemplateId) {
      data.data_model_template_id = dataModelTemplateId;
    }

    selectedNodeIds.forEach(nodeId => {
      data[`node.${nodeId}.include`] = true;
      if (nodeTaxonomicIds[nodeId]) {
        data[`node.${nodeId}.taxonomic_id`] = nodeTaxonomicIds[nodeId];
      }

      const fields = selectedFieldIds[nodeId] || new Set();
      fields.forEach(fieldId => {
        data[`node.${nodeId}.field.${fieldId}.include`] = true;
        if (fieldTaxonomicIds[nodeId]?.[fieldId]) {
          data[`node.${nodeId}.field.${fieldId}.taxonomic_id`] = fieldTaxonomicIds[nodeId][fieldId];
        }
      });
    });

    selectedRelationshipIds.forEach(relId => {
      data[`relationship.${relId}.include`] = true;
      if (relationshipTaxonomicIds[relId]) {
        data[`relationship.${relId}.taxonomic_id`] = relationshipTaxonomicIds[relId];
      }
    });

    selectedMeasureIds.forEach(measureId => {
      data[`measure.${measureId}.include`] = true;
      if (measureTaxonomicIds[measureId]) {
        data[`measure.${measureId}.taxonomic_id`] = measureTaxonomicIds[measureId];
      }
    });

    selectedDimensionIds.forEach(dimId => {
      data[`dimension.${dimId}.include`] = true;
      if (dimensionTaxonomicIds[dimId]) {
        data[`dimension.${dimId}.taxonomic_id`] = dimensionTaxonomicIds[dimId];
      }
    });

    return data;
  }, [templateName, taxonomicId, templateType, dataModelTemplateId, selectedNodeIds, selectedFieldIds, nodeTaxonomicIds, fieldTaxonomicIds, selectedRelationshipIds, relationshipTaxonomicIds, selectedMeasureIds, selectedDimensionIds, measureTaxonomicIds, dimensionTaxonomicIds]);

  const handleSave = async () => {
    if (!templateName || !taxonomicId || !templateType) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const data = buildFormData();
      if (isEditing) {
        await templateService.updateTemplate(templateId!, data);
        toast.success('Template updated successfully');
      } else {
        const result = await templateService.exportTemplate(data);
        toast.success('Template created successfully');
        if (result) {
          navigate(`/templates/edit/${result.id}`);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    } catch (err: any) {
      toast.error(err.payload?.msg || err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTaxonomicIds = async () => {
    setSavingTaxonomicIds(true);
    try {
      const data = buildFormData();
      if (isEditing) {
        await templateService.updateTemplate(templateId!, data, true);
      } else {
        await templateService.exportTemplate(data, true);
      }
      toast.success('Taxonomic IDs saved');
    } catch (err: any) {
      toast.error(err.payload?.msg || err.message || 'Failed to save taxonomic IDs');
    } finally {
      setSavingTaxonomicIds(false);
    }
  };

  if (!isTemplateManagementEnabled) {
    return <div className="p-6">Access denied</div>;
  }

  if (nodesLoading || relsLoading || measuresLoading || dimensionsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/develop">Develop</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/templates/admin">Templates</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{isEditing ? (templateName || 'Edit Template') : 'New Template'}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-3">
            <Layers className="h-6 w-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">
              {isEditing ? 'Edit Template' : 'Create Template'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={setAllTaxonomicIdsGlobal}
          >
            <Tags className="h-4 w-4 mr-1" />
            Set All Taxonomic IDs
          </Button>
          {isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveTaxonomicIds}
              disabled={savingTaxonomicIds}
            >
              {savingTaxonomicIds && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Save Taxonomic IDs Only
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            {isEditing ? 'Save New Version' : 'Create Template'}
          </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Template Info */}
        <div className="col-span-3 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Template Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Template Name</Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="My Template"
                />
              </div>
              <div className="space-y-2">
                <Label>Taxonomic ID</Label>
                <Input
                  value={taxonomicId}
                  onChange={(e) => setTaxonomicId(e.target.value)}
                  placeholder="PLB:::TPL:MY_TEMPLATE"
                />
                <p className="text-xs text-muted-foreground">
                  Format: XXX:::TPL:TEMPLATE_NAME
                </p>
              </div>
              <div className="space-y-2">
                <Label>Template Type</Label>
                <Select value={templateType} onValueChange={setTemplateType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DATA_MODEL">Data Model</SelectItem>
                    <SelectItem value="DATA_SOURCE">Data Source</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {templateType === 'DATA_SOURCE' && (
                <div className="space-y-2">
                  <Label>Data Model Template</Label>
                  <Select value={dataModelTemplateId} onValueChange={setDataModelTemplateId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select data model" />
                    </SelectTrigger>
                    <SelectContent>
                      {dataModelTemplates.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Nodes & Fields */}
        <div className="col-span-9 space-y-6">
          {/* Nodes Section */}
          <Card>
            <CardHeader>
              <CardTitle>Select Nodes & Columns</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {filteredNodes.map(node => {
                const isSelected = selectedNodeIds.has(node.id);
                const isExpanded = expandedNodes.has(node.id);
                const nodeFields = selectedFieldIds[node.id] || new Set();

                return (
                  <div key={node.id} className="border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleNode(node.id)}
                      />
                      <button
                        className="flex items-center gap-1 font-medium text-sm hover:text-primary"
                        onClick={() => toggleNodeExpanded(node.id)}
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {node.label || node.name}
                      </button>
                      {isSelected && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          {nodeFields.size} field(s) selected
                        </span>
                      )}
                    </div>

                    {isExpanded && (
                      <div className="mt-3 space-y-3 pl-6">
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => setAllTaxonomicIds(node.id)}>
                            Set All Taxonomic IDs
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => includeAllFields(node.id)}>
                            Include All Fields
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Node Taxonomic ID</Label>
                          <Input
                            value={nodeTaxonomicIds[node.id] || ''}
                            onChange={(e) => setNodeTaxonomicIds(prev => ({ ...prev, [node.id]: e.target.value }))}
                            placeholder="Taxonomic ID for this node"
                            className="h-8 text-sm"
                          />
                        </div>

                        {node.fields && node.fields.length > 0 && (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[50px]">Include</TableHead>
                                <TableHead>Column</TableHead>
                                <TableHead>Taxonomic ID</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {node.fields.map(field => {
                                if (!field.id) return null;
                                const fieldSelected = nodeFields.has(field.id);
                                return (
                                  <TableRow key={field.id}>
                                    <TableCell>
                                      <Checkbox
                                        checked={fieldSelected}
                                        onCheckedChange={() => toggleField(node.id, field.id!)}
                                      />
                                    </TableCell>
                                    <TableCell className="text-sm">{field.name || field.label}</TableCell>
                                    <TableCell>
                                      <Input
                                        value={fieldTaxonomicIds[node.id]?.[field.id!] || ''}
                                        onChange={(e) => {
                                          setFieldTaxonomicIds(prev => ({
                                            ...prev,
                                            [node.id]: {
                                              ...(prev[node.id] || {}),
                                              [field.id!]: e.target.value,
                                            },
                                          }));
                                        }}
                                        placeholder="Taxonomic ID"
                                        className="h-7 text-xs"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Relationships Section */}
          {filteredRelationships.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Select Relationships</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">Include</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Parent</TableHead>
                      <TableHead>Child</TableHead>
                      <TableHead>Taxonomic ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRelationships.map(rel => (
                      <TableRow key={rel.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedRelationshipIds.has(rel.id)}
                            onCheckedChange={() => toggleRelationship(rel.id)}
                          />
                        </TableCell>
                        <TableCell className="text-sm">{rel.name}</TableCell>
                        <TableCell className="text-sm">{nodeNameMap[rel.parent_node_id] || rel.parent_node_id}</TableCell>
                        <TableCell className="text-sm">{nodeNameMap[rel.child_node_id] || rel.child_node_id}</TableCell>
                        <TableCell>
                          <Input
                            value={relationshipTaxonomicIds[rel.id] || ''}
                            onChange={(e) => setRelationshipTaxonomicIds(prev => ({ ...prev, [rel.id]: e.target.value }))}
                            placeholder="Taxonomic ID"
                            className="h-7 text-xs"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Dimensions Section */}
          {templateType === 'DATA_MODEL' && dimensionsData?.records && dimensionsData.records.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Select Dimensions</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">Include</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Taxonomic ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dimensionsData.records.map(dim => (
                      <TableRow key={dim.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedDimensionIds.has(dim.id)}
                            onCheckedChange={() => toggleDimension(dim.id)}
                          />
                        </TableCell>
                        <TableCell className="text-sm">{dim.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {nodeNameMap[dim.pipeline_node_id] || dim.pipeline_node_id}
                        </TableCell>
                        <TableCell>
                          <Input
                            value={dimensionTaxonomicIds[dim.id] || ''}
                            onChange={(e) => setDimensionTaxonomicIds(prev => ({ ...prev, [dim.id]: e.target.value }))}
                            placeholder="Taxonomic ID"
                            className="h-7 text-xs"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Measures Section */}
          {templateType === 'DATA_MODEL' && measuresData?.records && measuresData.records.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Select Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">Include</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Taxonomic ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {measuresData.records.map(measure => (
                      <TableRow key={measure.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedMeasureIds.has(measure.id)}
                            onCheckedChange={() => toggleMeasure(measure.id)}
                          />
                        </TableCell>
                        <TableCell className="text-sm">{measure.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {measure.metric_type}
                        </TableCell>
                        <TableCell>
                          <Input
                            value={measureTaxonomicIds[measure.id] || ''}
                            onChange={(e) => setMeasureTaxonomicIds(prev => ({ ...prev, [measure.id]: e.target.value }))}
                            placeholder="Taxonomic ID"
                            className="h-7 text-xs"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <AlertDialog open={showTaxIdAlert} onOpenChange={setShowTaxIdAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Template Taxonomic ID Required</AlertDialogTitle>
            <AlertDialogDescription>
              Please set a taxonomic ID on the template first (in the Template Info panel on the left).
              Node and field taxonomic IDs are derived from the template's taxonomic ID.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
